import { Box, Flex, Grid, Skeleton, Text } from "@chakra-ui/react";
import { formatMoneyWithSymbol, MenuItem } from "@verivest/shared-components";
import { GroupedInvestmentModel } from "@verivest/shared-components/src/api/models";
import { formatDateMMDDYYYY } from "@verivest/shared-components/src/utils/dates";
import { getOpportunityFeaturedImage } from "@verivest/shared-components/src/utils/opportunities";
import React from "react";
import { ActiveInvestmentCard } from "../../../horizontal-cards/ActiveInvestmentCard";
import { InvestmentCardLabelValue } from "../../../horizontal-cards/InvestmentCardLabelValue";
import { WhiteLabelSortBy } from "../../WhiteLabelSortBy/WhiteLabelSortBy";
import {
  SortDirectionsEnum,
  SortOptionType,
  WLManagerNotPendingInvestmentsProps,
} from "./WLManagerNotPendingInvestments.types";
import { ImportedTag } from "../../../../general/imported-investment/ImportedTag";

export const sortOption = {
  Active: {
    sortBy: "investedDate",
    sortDirection: SortDirectionsEnum.active,
    label: "Active",
  },
  Realized: {
    sortBy: "realizedDate",
    sortDirection: SortDirectionsEnum.realized,
    label: "Realized",
  },
};

const sortInvestments = (
  a: GroupedInvestmentModel,
  b: GroupedInvestmentModel,
  sortBy: SortOptionType,
) => {
  const sortOrder = sortBy.sortDirection === SortDirectionsEnum.active ? 1 : -1;
  const dateA = a.realizedDate || a.investedDate || "";
  const dateB = b.realizedDate || b.investedDate || "";

  if (a.realizedDate && !b.realizedDate) return sortOrder;
  if (!a.realizedDate && b.realizedDate) return -sortOrder;
  return dateA === dateB
    ? 0
    : (new Date(dateB).getTime() - new Date(dateA).getTime()) * sortOrder;
};

export const WLManagerNotPendingInvestments = ({
  investments: activeInvestments,
  updateFilter,
  filter,
}: WLManagerNotPendingInvestmentsProps) => {
  const setFilterOption = (filterOption: SortOptionType) => {
    updateFilter(filterOption);
  };

  const sortedInvestments = activeInvestments?.sort((a, b) =>
    sortInvestments(a, b, filter),
  );

  return (
    <Box w="100%">
      {!!sortedInvestments?.length && (
        <Flex justifyContent={"space-between"}>
          <Text textStyle="h2" mb={5}>
            Active & Realized Investments
          </Text>
          <Box>
            <WhiteLabelSortBy activeSort={filter.label}>
              <>
                <MenuItem
                  onClick={() => setFilterOption(sortOption.Active)}
                  px={8}
                >
                  {sortOption.Active.label}
                </MenuItem>
                <MenuItem
                  onClick={() => setFilterOption(sortOption.Realized)}
                  px={8}
                >
                  {sortOption.Realized.label}
                </MenuItem>
              </>
            </WhiteLabelSortBy>
          </Box>
        </Flex>
      )}
      <Box w="100%">
        {activeInvestments === undefined && (
          <>
            {new Array(10).fill(null).map((_, index) => (
              <Skeleton height="170px" w={"100%"} key={index} mb={2} />
            ))}
          </>
        )}
        {sortedInvestments?.map((investment, index) => (
          <ActiveInvestmentCard
            key={`investment-card-not-pending-${index}`}
            imageUrl={
              investment.offering?.opportunity
                ? getOpportunityFeaturedImage(investment.offering.opportunity)
                : undefined
            }
            header={
              <>
                {investment.offering?.name}
                <ImportedTag
                  isImported={investment.hasImports}
                  containerProps={{ mt: 1 }}
                />
              </>
            }
            accountId={investment.account?.id as string}
            manager={investment.offering?.opportunity?.manager}
            offeringId={investment.offering?.id as string}
          >
            <Flex className={"flex-col sm:flex-row"}>
              <Grid className={"grid-cols-1 sm:grid-cols-[2fr_3fr] gap-2"}>
                <InvestmentCardLabelValue
                  label="Account"
                  value={
                    investment.account?.name ||
                    investment.account?.nameOfRecord ||
                    ""
                  }
                />
                <InvestmentCardLabelValue
                  label="Invested Date"
                  value={formatDateMMDDYYYY(investment.investedDate) || "-"}
                />
                <InvestmentCardLabelValue
                  label="Realized Date"
                  value={formatDateMMDDYYYY(investment.realizedDate) || "-"}
                />
              </Grid>
              <Grid className={"grid-cols-1 sm:grid-cols-[2fr_3fr] gap-2"}>
                <InvestmentCardLabelValue
                  label={
                    !investment.offering?.hideDistributions
                      ? "Distributions"
                      : " "
                  }
                  value={
                    !investment.offering?.hideDistributions
                      ? formatMoneyWithSymbol(investment.distributions || 0)
                      : ""
                  }
                />
                <InvestmentCardLabelValue
                  label="Capital Committed"
                  value={formatMoneyWithSymbol(investment.committedValue || 0)}
                />
                <InvestmentCardLabelValue
                  label="Capital Contributed"
                  value={formatMoneyWithSymbol(
                    investment.contributionsValue || 0,
                  )}
                />
              </Grid>
            </Flex>
          </ActiveInvestmentCard>
        ))}
      </Box>
    </Box>
  );
};
